// Trackeamento > Meu site: endereço, plataforma, código para colar, passo a passo, verificação e
// mensagem pronta para quem cuida do site.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, CircleDashed, ExternalLink, Info, Mail, MessageCircle, RefreshCw, ScanSearch, XCircle } from 'lucide-react'
import type { SiteCheckDto, SitePlatform, TrackingSetupDto } from '@impulso/shared/api/tracking'
import { errorMessage } from '../../../lib/api'
import { useCan, useCompany } from '../../../lib/company'
import { ErrorState, Loading, PageHead } from '../../../ui'
import { useToast } from '../../../ui/toast'
import { useCheckSite, useTracking, useUpdateTracking } from '../../../features/tracking/api'
import { CopyBox, fmtDateTime, PlatformMark, relativeTime, TrackingStatusBadge } from '../../../features/tracking/components'
import { developerMessage, guideFor, PLATFORM_GUIDES } from '../../../features/tracking/guides'

export default function TrackingSite() {
  const company = useCompany()
  const q = useTracking(company.id, { poll: true })
  if (q.isLoading) return <Loading lines={6} />
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  return <SiteSetup setup={q.data!} />
}

function SiteSetup({ setup }: { setup: TrackingSetupDto }) {
  const company = useCompany()
  const canEdit = useCan('company.edit_settings')
  const update = useUpdateTracking(company.id)
  const check = useCheckSite(company.id)
  const toast = useToast()
  const [url, setUrl] = useState(setup.siteUrl ?? '')
  useEffect(() => setUrl(setup.siteUrl ?? ''), [setup.siteUrl])
  const platform = setup.platform
  const guide = guideFor(platform)
  const result = check.data ?? setup.lastCheck

  const save = (body: Parameters<typeof update.mutate>[0], msg?: string) =>
    update.mutate(body, { onSuccess: () => msg && toast(msg), onError: (e) => toast(errorMessage(e)) })

  const runCheck = () => {
    const target = url.trim() || undefined
    check.mutate(target, { onError: (e) => toast(errorMessage(e)) })
  }

  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Meu site" sub="Instale a tag do Seu Impulso no seu site atual. É uma linha de código, uma vez só. Depois disso, tudo é configurado por aqui." />

      {setup.siteMode && setup.siteMode !== 'own_site' && (
        <div className="callout amber">
          <Info size={18} color="var(--amber)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div className="small">Hoje o destino dos anúncios está como <strong>página feita aqui</strong>. Se você também quer medir o seu site, siga os passos abaixo.
            {canEdit && <button className="btn sm" style={{ marginLeft: 8 }} onClick={() => save({ siteMode: 'own_site' }, 'Destino: meu site.')}>Usar meu site como destino</button>}
          </div>
        </div>
      )}

      <section className="card stack" style={{ gap: 14 }}>
        <div className="row between wrap" style={{ gap: 8 }}>
          <h2>1. Endereço do site</h2>
          <TrackingStatusBadge status={setup.status === 'not_started' ? 'waiting' : setup.status} />
        </div>
        <form className="row wrap" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); save({ siteUrl: url.trim() || null, ...(setup.siteMode ? {} : { siteMode: 'own_site' }) }, 'Endereço salvo.') }}>
          <input className="input" style={{ flex: 1, minWidth: 220 }} placeholder="www.seusite.com.br" value={url} onChange={(e) => setUrl(e.target.value)} disabled={!canEdit} aria-label="Endereço do site" inputMode="url" />
          {canEdit && <button className="btn" type="submit" disabled={update.isPending || url.trim() === (setup.siteUrl ?? '')}>Salvar</button>}
        </form>
      </section>

      <section className="card stack" style={{ gap: 14 }}>
        <h2>2. Onde o seu site foi feito?</h2>
        {result?.detectedPlatform && result.detectedPlatform !== platform && (
          <div className="callout green small">Pelo que vimos, seu site parece ser <strong>{guideFor(result.detectedPlatform)?.name}</strong>.{canEdit && <button className="btn sm" style={{ marginLeft: 8 }} onClick={() => save({ platform: result.detectedPlatform })}>Usar esse</button>}</div>
        )}
        <div className="platform-grid" role="radiogroup" aria-label="Plataforma do site">
          {PLATFORM_GUIDES.map((g) => (
            <button key={g.id} type="button" role="radio" aria-checked={platform === g.id} className={`platform-opt ${platform === g.id ? 'on' : ''}`} disabled={!canEdit} onClick={() => save({ platform: g.id as SitePlatform })}>
              <PlatformMark guide={g} size={30} />
              <span>{g.name}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="card stack" style={{ gap: 14 }}>
        <h2>3. Cole este código no seu site</h2>
        <p className="small muted">Ele vai dentro do <code>&lt;head&gt;</code> de todas as páginas. Não muda nada no visual do site.</p>
        <CopyBox code value={setup.snippet} label="Código da tag do Seu Impulso" />
        {guide ? (
          <div className="stack mt-8" style={{ gap: 12 }}>
            <div className="row" style={{ gap: 10 }}><PlatformMark guide={guide} size={28} /><h3>Passo a passo no {guide.name}</h3></div>
            <ol className="steps-list">{guide.steps.map((s) => <li key={s}><span>{s}</span></li>)}</ol>
            {guide.tips.map((t) => <div key={t} className="callout small"><Info size={16} color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} /><span>{t}</span></div>)}
            {guide.helpUrl && <a className="small strong row" style={{ gap: 4, color: 'var(--primary)' }} href={guide.helpUrl} target="_blank" rel="noreferrer">Ajuda oficial <ExternalLink size={13} /></a>}
            <p className="small muted">Os nomes dos menus podem mudar um pouco conforme a versão da plataforma.</p>
          </div>
        ) : <p className="small muted">Escolha a plataforma acima para ver o passo a passo.</p>}
      </section>

      <section className="card stack" style={{ gap: 14 }}>
        <div className="row between wrap" style={{ gap: 8 }}>
          <h2>4. Confira se deu certo</h2>
          <button className="btn primary" disabled={check.isPending || (!url.trim() && !setup.siteUrl)} onClick={runCheck}>
            {check.isPending ? <><RefreshCw size={15} className="spin" /> Verificando…</> : <><ScanSearch size={15} /> Verificar meu site</>}
          </button>
        </div>
        {!url.trim() && !setup.siteUrl && <p className="small muted">Informe o endereço do site no passo 1 para poder verificar.</p>}
        <SignalBox setup={setup} />
        {result && <CheckResult result={result} />}
      </section>

      <DeveloperCard setup={setup} />

      <p className="small muted">Prefere uma página nova, já pronta para anúncio? <Link className="strong" style={{ color: 'var(--primary)' }} to={`/app/${company.id}/trackeamento/landing-pages`}>Conheça as páginas</Link>.</p>
    </div>
  )
}

function SignalBox({ setup }: { setup: TrackingSetupDto }) {
  const ok = !!setup.lastSeenAt
  return (
    <div className={`callout ${ok ? 'green' : ''}`}>
      {ok ? <CheckCircle2 size={20} color="var(--green)" style={{ flexShrink: 0 }} /> : <CircleDashed size={20} className="spin" color="var(--primary)" style={{ flexShrink: 0, animationDuration: '3s' }} />}
      <div className="small">
        {ok
          ? <><strong>A tag está funcionando.</strong> Último sinal {relativeTime(setup.lastSeenAt)}{setup.lastSeenUrl ? <> em <span className="strong">{setup.lastSeenUrl}</span></> : null}.</>
          : <><strong>Aguardando o primeiro sinal.</strong> Depois de instalar, abra o seu site em outra aba. Esta tela atualiza sozinha em alguns segundos.</>}
      </div>
    </div>
  )
}

function CheckResult({ result }: { result: SiteCheckDto }) {
  if (!result.ok) {
    return <div className="form-error" role="alert">Não conseguimos abrir {result.url}: {result.error}</div>
  }
  const rows: Array<{ ok: boolean; title: string; detail: string; neutral?: boolean }> = [
    { ok: result.found.impulsoTag, title: 'Tag do Seu Impulso', detail: result.found.impulsoTag ? 'Encontrada no código da página.' : result.found.gtm ? 'Não aparece no HTML. Se instalou pelo Google Tag Manager, isso é normal: confira os sinais acima.' : 'Ainda não encontramos. Confira se colou e salvou (e limpe o cache, se tiver).' },
    { ok: result.found.metaPixel, neutral: !result.found.metaPixel, title: 'Pixel da Meta', detail: result.ids.metaPixelIds.length ? `Já instalado no site: ${result.ids.metaPixelIds.join(', ')}` : 'Não há pixel no HTML. Tudo bem: a tag do Seu Impulso carrega o pixel informado em Eventos e conversões.' },
    { ok: result.found.googleTag, neutral: !result.found.googleTag, title: 'Tag do Google', detail: result.ids.googleTagIds.length ? `Já instalada no site: ${result.ids.googleTagIds.join(', ')}` : 'Não há tag do Google no HTML. A tag do Seu Impulso pode carregar a sua (configure em Eventos e conversões).' },
  ]
  if (result.found.gtm) rows.push({ ok: true, neutral: true, title: 'Google Tag Manager', detail: `Encontrado: ${result.ids.gtmIds.join(', ')}. Códigos dentro dele não aparecem nesta verificação.` })
  return (
    <div>
      <div className="small muted">Verificado em {fmtDateTime(result.checkedAt)} · {result.url}</div>
      <div className="mt-8">
        {rows.map((r) => (
          <div key={r.title} className="check-row">
            {r.ok ? <CheckCircle2 size={20} color="var(--green)" /> : r.neutral ? <Info size={20} color="var(--muted)" /> : <XCircle size={20} color="var(--red)" />}
            <div><div className="strong">{r.title}</div><div className="small muted">{r.detail}</div></div>
          </div>
        ))}
      </div>
    </div>
  )
}

function DeveloperCard({ setup }: { setup: TrackingSetupDto }) {
  const company = useCompany()
  const msg = developerMessage(company.name, setup.snippet, setup.siteUrl)
  return (
    <section className="card stack" style={{ gap: 12 }}>
      <h2>Mandar para quem cuida do site</h2>
      <p className="small muted">Se outra pessoa ou agência mexe no seu site, envie esta mensagem pronta. Ela tem o código e as instruções.</p>
      <CopyBox value={msg} label="Mensagem para o responsável pelo site" rows={8} />
      <div className="row wrap" style={{ gap: 8 }}>
        <a className="btn sm" href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer"><MessageCircle size={14} /> Enviar pelo WhatsApp</a>
        <a className="btn sm" href={`mailto:?subject=${encodeURIComponent(`Código de rastreamento - ${company.name}`)}&body=${encodeURIComponent(msg)}`}><Mail size={14} /> Enviar por e-mail</a>
      </div>
    </section>
  )
}
